import React, { useState, useEffect } from 'react';
import { userService } from '../services/api';
import ConfirmationModal from './ConfirmationModal';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user'
  });
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await userService.getAll();
      setUsers(response.data);
    } catch (error) {
      setError('Failed to load users');
      console.error('Load users error:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.username || !formData.role) {
      setError('Username and role are required');
      return;
    }

    if (!editingUser && !formData.password) {
      setError('Password is required for new users');
      return;
    }

    try {
      if (editingUser) {
        await userService.update(editingUser.id, {
          username: formData.username,
          role: formData.role
        });
        setSuccessMessage('User updated successfully');
      } else {
        await userService.create(formData);
        setSuccessMessage('User created successfully');
      }
      
      setTimeout(() => setSuccessMessage(''), 3000); // Clear after 3 seconds
      setShowForm(false);
      setEditingUser(null);
      setFormData({ username: '', password: '', role: 'user' });
      await loadUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save user');
      console.error('Save user error:', error);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role
    });
    setShowForm(true);
  };

  const handleDelete = async (user) => {
    setShowDeleteConfirm(user);
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;

    try {
      await userService.delete(showDeleteConfirm.id);
      setSuccessMessage(`User "${showDeleteConfirm.username}" deleted successfully`);
      setTimeout(() => setSuccessMessage(''), 3000); // Clear after 3 seconds
      await loadUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete user');
      console.error('Delete user error:', error);
    }
    setShowDeleteConfirm(null);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordData.password) {
      setError('Password is required');
      return;
    }

    if (passwordData.password !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await userService.updatePassword(showPasswordForm.id, passwordData.password);
      setShowPasswordForm(null);
      setPasswordData({ password: '', confirmPassword: '' });
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update password');
      console.error('Update password error:', error);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({ username: '', password: '', role: 'user' });
    setError('');
  };

  const resetPasswordForm = () => {
    setShowPasswordForm(null);
    setPasswordData({ password: '', confirmPassword: '' });
    setError('');
  };

  if (loading) {
    return <div>Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h3>User Management</h3>
        <button 
          className="btn" 
          style={{ backgroundColor: 'var(--primary-color)' }}
          onClick={() => setShowForm(true)}
        >
          Add New User
        </button>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Add/Edit User Form */}
      {showForm && (
        <div style={{ 
          background: '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '4px', 
          marginBottom: '2rem',
          border: '1px solid #dee2e6'
        }}>
          <h4>{editingUser ? 'Edit User' : 'Add New User'}</h4>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Username:</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                required
              />
            </div>
            
            {!editingUser && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Password:</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  required
                />
              </div>
            )}
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Role:</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            
            <div>
              <button type="submit" className="btn" style={{ backgroundColor: '#007bff', marginRight: '0.5rem' }}>
                {editingUser ? 'Update User' : 'Create User'}
              </button>
              <button type="button" className="btn" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Change Password Form */}
      {showPasswordForm && (
        <div style={{ 
          background: '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '4px', 
          marginBottom: '2rem',
          border: '1px solid #dee2e6'
        }}>
          <h4>Change Password for {showPasswordForm.username}</h4>
          <form onSubmit={handlePasswordSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>New Password:</label>
              <input
                type="password"
                value={passwordData.password}
                onChange={(e) => setPasswordData({...passwordData, password: e.target.value})}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                required
              />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Confirm Password:</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                required
              />
            </div>
            
            <div>
              <button type="submit" className="btn" style={{ backgroundColor: '#007bff', marginRight: '0.5rem' }}>
                Update Password
              </button>
              <button type="button" className="btn" onClick={resetPasswordForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table/Cards */}
      <div style={{ marginTop: '2rem' }}>
        {users.length === 0 ? (
          <p>No users found.</p>
        ) : (
          <>
            {/* Desktop Table View */}
            <div style={{ display: isMobile ? 'none' : 'block', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #dee2e6' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Username</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Role</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Created</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '0.75rem' }}>{user.username}</td>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{ 
                          color: '#6c757d', 
                          fontSize: '0.85em', 
                          fontStyle: 'italic',
                          opacity: 0.8
                        }}>
                          {user.role}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <button 
                          className="btn" 
                          style={{ backgroundColor: '#007bff', marginRight: '0.5rem', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                          onClick={() => handleEdit(user)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn"
                          style={{ backgroundColor: '#28a745', marginRight: '0.5rem', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                          onClick={() => setShowPasswordForm(user.id)}
                        >
                          Reset Password
                        </button>
                        <button 
                          className="btn"
                          style={{ backgroundColor: '#dc3545', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                          onClick={() => handleDelete(user)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div style={{ display: isMobile ? 'block' : 'none' }}>
              {users.map((user) => (
                <div key={user.id} style={{
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                      {user.username}
                    </div>
                    <span style={{ 
                      color: '#6c757d', 
                      fontSize: '0.85em', 
                      fontStyle: 'italic',
                      opacity: 0.8
                    }}>
                      {user.role}
                    </span>
                  </div>
                  
                  <div style={{ marginBottom: '1rem', color: '#6c757d', fontSize: '0.9rem' }}>
                    Created: {new Date(user.created_at).toLocaleDateString()}
                  </div>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <button 
                      className="btn" 
                      style={{ 
                        backgroundColor: '#007bff', 
                        fontSize: '0.875rem', 
                        padding: '0.5rem 1rem',
                        flex: '1',
                        minWidth: '80px'
                      }}
                      onClick={() => handleEdit(user)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn"
                      style={{ 
                        backgroundColor: '#28a745', 
                        fontSize: '0.875rem', 
                        padding: '0.5rem 1rem',
                        flex: '1',
                        minWidth: '100px'
                      }}
                      onClick={() => setShowPasswordForm(user.id)}
                    >
                      Reset Password
                    </button>
                    <button 
                      className="btn"
                      style={{ 
                        backgroundColor: '#dc3545', 
                        fontSize: '0.875rem', 
                        padding: '0.5rem 1rem',
                        flex: '1',
                        minWidth: '80px'
                      }}
                      onClick={() => handleDelete(user)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Toast Success Message */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: '#28a745',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '4px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          zIndex: 1000,
          animation: 'fadeInOut 3s ease-in-out forwards'
        }}>
          {successMessage}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isVisible={!!showDeleteConfirm}
        title="FireWatch"
        message={`Delete user "${showDeleteConfirm?.username}"?`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isDestructive={true}
      />
    </div>
  );
}

export default UserManagement;